import React, { useState } from 'react';
import { toast } from 'sonner';
import { Gavel, FileText, CheckCircle2, AlertTriangle, ChevronRight, Sparkles, Download } from 'lucide-react';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { KpiCard } from '@/components/shared/KpiCard';
import { StatusChip } from '@/components/shared/StatusChip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { LEGAL_CASES } from '@/data/seed';
import { formatSAR } from '@/lib/format';
import { useApp } from '@/context/AppContext';
import { usePageTitle } from '@/lib/usePageTitle';

export default function LegalWorkbench() {
  usePageTitle('Legal Workbench');
  const { pushEvent } = useApp();
  const [selected, setSelected] = useState(null);
  const [generated, setGenerated] = useState(false);

  const generatePackage = () => {
    setGenerated(true);
    toast.success('Legal package generated', { description: `${selected.caseId} v1 • routed for client approval` });
    pushEvent({ type: 'LegalPackageGenerated', src: 'Alwasl Recovery OS', dst: 'Legal Export Mock', status: 'success', payload: `${selected.caseId} package v1`, caseId: selected.caseId });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Legal workbench"
        title="Legal escalation & package readiness"
        subtitle="Score legal candidates, complete document checklists and export Najiz/Tanfeeth-style packages — with full evidence trail."
        actions={<Button variant="outline" size="sm"><FileText className="h-4 w-4 mr-1.5" />Templates</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Legal candidates" value={412} format="num" trend={1.2} icon={Gavel} />
        <KpiCard label="Pre-Legal Review" value={68} format="num" trend={-3} />
        <KpiCard label="Filed — Court" value={94} format="num" trend={+8} />
        <KpiCard label="Pipeline exposure" value={38400000} format="sar" trend={+4.6} accent />
      </div>

      <div className="surface-card overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Legal queue</p>
            <h3 className="text-base font-semibold mt-0.5">Ranked by readiness & exposure</h3>
          </div>
          <Badge variant="outline" className="text-[10px]">Sorted by legal readiness</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
              <tr>
                <th scope="col" className="text-left px-5 py-2.5 font-medium">Case</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Debtor</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Outstanding</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Readiness</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Missing docs</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Legal status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {LEGAL_CASES.map((c) => (
                <tr key={c.caseId} className="border-t border-border hover:bg-muted/40">
                  <td className="px-5 py-3 font-medium">{c.caseId}</td>
                  <td className="px-3 py-3">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">{c.product} • {c.region}</p>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num font-semibold">{formatSAR(c.amount)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <Progress value={c.legalReadiness} className="h-1.5 w-20" />
                      <span className="text-xs font-mono-num">{c.legalReadiness}/100</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {c.docsMissing.length === 0 ? <Badge variant="outline" className="text-success border-success/40 bg-success/10">Complete</Badge> : (
                      <div className="flex flex-wrap gap-1">
                        {c.docsMissing.map((m) => (<Badge key={m} variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/40">{m}</Badge>))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3"><StatusChip status={c.legalStatus} /></td>
                  <td className="px-3 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setSelected(c); setGenerated(false); }} className="text-primary" data-testid={`legal-open-${c.caseId}`}>Review package <ChevronRight className="h-3.5 w-3.5 ml-1" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Gavel className="h-5 w-5 text-primary" /> {selected.caseId} — {selected.name}</DialogTitle>
                <DialogDescription>Generate court-ready legal package with audit trail.</DialogDescription>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4 py-2">
                <div className="p-4 rounded-lg bg-muted/60 border border-border">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Legal readiness</p>
                  <p className="font-mono-num text-3xl font-semibold mt-1">{selected.legalReadiness}<span className="text-base text-muted-foreground">/100</span></p>
                  <Progress value={selected.legalReadiness} className="h-1.5 mt-2" />
                </div>
                <div className="p-4 rounded-lg bg-muted/60 border border-border">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Exposure</p>
                  <p className="font-mono-num text-3xl font-semibold mt-1">{formatSAR(selected.amount)}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">Delinquency {selected.delinquencyDays}d • {selected.product}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-primary mb-2">Document checklist</p>
                <ul className="space-y-1.5 text-sm">
                  {[
                    { d: 'Loan contract / agreement', ok: true },
                    { d: 'Statement of account', ok: !selected.docsMissing.includes('Statement of account') },
                    { d: 'Notice of default', ok: true },
                    { d: 'Communication log (12 months)', ok: true },
                    { d: 'Power of attorney', ok: !selected.docsMissing.includes('Power of attorney') },
                    { d: 'Client authorization', ok: !selected.docsMissing.includes('Client authorization') },
                  ].map((r) => (
                    <li key={r.d} className="flex items-center gap-2">
                      {r.ok ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-warning" />}
                      <span className={r.ok ? '' : 'text-warning'}>{r.d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                {!generated ? (
                  <>
                    <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
                    <Button onClick={generatePackage} className="bg-primary text-primary-foreground hover:bg-primary/90">
                      <Sparkles className="h-4 w-4 mr-1.5" />Generate legal package
                    </Button>
                  </>
                ) : (
                  <>
                    <Badge className="bg-success/15 text-success border-success/40" variant="outline">Package v1 ready</Badge>
                    <Button variant="outline"><Download className="h-4 w-4 mr-1.5" />Download PDF</Button>
                    <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90">Send for client approval</Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
